import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HydraLink } from '@/lib/hydraLinks';

// ============================================================================
// FileViewer — УНИВЕРСАЛЬНЫЙ просмотрщик файлов (Этап 5 §18; SRP):
// тип по расширению имени; md — свой escape-first конвертер (НИКАКОГО
// dangerouslySetInnerHTML сырого HTML из файла); код/текст — mono;
// изображения — data URL/url; json — pretty; pdf — iframe.
// ============================================================================

export interface FileViewerProps {
    name: string;
    /** текстовое содержимое (md/txt/code/json) */
    content?: string;
    /** base64 (изображения/pdf); без data: префикса */
    base64?: string;
    /** прямая ссылка (изображения/pdf) — альтернатива base64 */
    url?: string;
    className?: string;
}

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'];
const CODE_EXT = ['ts', 'tsx', 'js', 'jsx', 'go', 'py', 'json', 'sql', 'sh', 'bat', 'ps1', 'yaml', 'yml', 'xml', 'html', 'css', 'bsl', 'txt', 'log', 'csv', 'ini'];

export function extOf(name: string): string {
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

function mimeByExt(ext: string): string {
    const m: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
        svg: 'image/svg+xml', webp: 'image/webp', bmp: 'image/bmp', ico: 'image/x-icon',
        pdf: 'application/pdf',
    };
    return m[ext] || 'application/octet-stream';
}

/** Детерминированный escape-first markdown → JSX. Поддержка: #–#### заголовки,
 *  списки -/*, код-fence ```, inline-code `, **bold**, *italic*, [link](url),
 *  --- (hr), пустая строка = абзац. HTML из файла НЕ исполняется (escape-first). */
// MarkdownView — react-markdown (+GFM): сырой HTML из файла ОТКЛЮЧЁН по
// умолчанию (escape-first гарантируется библиотекой — БЕЗ rehype-raw, НИКОГДА
// не добавлять: raw-рендер HTML = XSS из файлов/справки); ссылки рендерятся
// через ЕДИНЫЙ HydraLink (SSoT ссылок платформы: doc:/object:/route/external).
const markdownComponents = {
    a: (props: any) => <HydraLink href={props.href || '#'}>{props.children}</HydraLink>,
};

export function MarkdownView({ text }: { text: string }) {
    return (
        <div className="text-sm leading-relaxed [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:border-b [&_h1]:pb-1 [&_h1]:my-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h4]:text-sm [&_h4]:font-semibold [&_ul]:list-disc [&_ul]:list-inside [&_ul]:space-y-0.5 [&_ol]:list-decimal [&_ol]:list-inside [&_ol]:space-y-0.5 [&_p]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/50 [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_hr]:border-border [&_input[type=checkbox]]:mr-1">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {String(text ?? '')}
            </ReactMarkdown>
        </div>
    );
}

export function FileViewer({ name, content, base64, url, className }: FileViewerProps) {
    const ext = extOf(name);

    if (IMAGE_EXT.includes(ext)) {
        const src = url || (base64 ? `data:${mimeByExt(ext)};base64,${base64}` : '');
        if (!src) return <div className="text-xs text-muted-foreground">нет содержимого</div>;
        return <img src={src} alt={name} className={`max-w-full max-h-full object-contain ${className || ''}`} />;
    }

    if (ext === 'pdf') {
        const src = url || (base64 ? `data:application/pdf;base64,${base64}` : '');
        if (!src) return <div className="text-xs text-muted-foreground">нет содержимого</div>;
        return <iframe src={src} title={name} className={`w-full h-full min-h-[400px] rounded-md border ${className || ''}`} />;
    }

    if (ext === 'json' && content) {
        let pretty = content;
        try { pretty = JSON.stringify(JSON.parse(content), null, 2); } catch { /* не JSON — как есть */ }
        return <pre className={`text-xs font-mono whitespace-pre-wrap break-all bg-muted rounded-md p-3 overflow-auto ${className || ''}`}>{pretty}</pre>;
    }

    if (ext === 'md' && content !== undefined) {
        return <div className={className}><MarkdownView text={content} /></div>;
    }

    if ((CODE_EXT.includes(ext) || content !== undefined) && content !== undefined) {
        return <pre className={`text-xs font-mono whitespace-pre-wrap break-words bg-muted rounded-md p-3 overflow-auto ${className || ''}`}>{content}</pre>;
    }

    return <div className="text-xs text-muted-foreground">Предпросмотр для «.{ext || '?'}» не поддерживается — скачайте файл.</div>;
}
